import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Autocomplete,
  TextField,
  createFilterOptions,
  InputAdornment,
} from "@mui/material";
import { Image, Anchor } from "hesus-ui-kit";
import { useDebounce } from "use-debounce";
import searchIcon from "@icons/ic-search-blue.svg";
import { addressSingleLine } from "@utils/utils";
import { autoSearchInputStyles } from "@utils/extra-style";
import {
  Block,
  BlockHeader,
  CrossIcon,
  BlockBody,
  InfoLine,
  InfoLabel,
  PlaceholderText,
} from "./CompanySite.styled";

const SelectedCompany = ({
  t,
  company,
  countryId,
  countryList,
  onSelectCompany,
  onRemoveCompany,
  error,
}) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!countryId || debouncedSearch.length < 1) {
      setOptions([]);
      return;
    }
    setLoading(true);
    axios
      .get(
        `/general/search?countries=[${countryId}]&objectTypes=["client"]&dependancies=${JSON.stringify(
          ["billingAddress", "shippingAddress"]
        )}&search=${encodeURIComponent(debouncedSearch)}&limit=50`
      )
      .then((response) => {
        setOptions(response.data?.companies || []);
      })
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  }, [debouncedSearch, countryId]);

  if (company?.id) {
    return (
      <Block>
        <BlockHeader $isSelected>
          {t("CLIENT_INVOICING_SELECTED_COMPANY")}
          <CrossIcon onClick={onRemoveCompany} aria-hidden="true">
            &times;
          </CrossIcon>
        </BlockHeader>
        <BlockBody>
          <InfoLine>
            <InfoLabel>{t("CLIENT_INVOICE_COMPANY")} : </InfoLabel>
            <Anchor
              url={`/company/view/${company.id}/general-info`}
              target="_blank"
              rel="noreferrer"
              styles={{ color: "#1bace4", textDecoration: "underline" }}
            >
              {`#${company.id} - ${company.name}`}
            </Anchor>
          </InfoLine>
          <InfoLine>
            <InfoLabel>{t("CLIENT_INVOICE_IDENTICATION_NUMBER")} : </InfoLabel>
            {company.registrationNumber}
          </InfoLine>
          {company.billingAddress && (
            <InfoLine>
              <InfoLabel>{t("ADDRESS")} : </InfoLabel>
              <span
                dangerouslySetInnerHTML={{
                  __html: addressSingleLine(
                    countryId,
                    company.billingAddress,
                    countryList
                  ),
                }}
              />
            </InfoLine>
          )}
        </BlockBody>
      </Block>
    );
  }

  return (
    <Block>
      <BlockHeader>{t("CLIENT_INVOICING_SELECTED_COMPANY")}</BlockHeader>
      <BlockBody>
        <Autocomplete
          id="invoice-company-search"
          fullWidth
          loading={loading}
          options={options}
          getOptionLabel={(option) => `#${option.id} - ${option.name}`}
          filterOptions={(opts, params) => createFilterOptions()(opts, params)}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === "input") setSearch(newInputValue || "");
          }}
          onChange={(event, newValue) => {
            if (newValue) onSelectCompany(newValue);
          }}
          value={null}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("SEARCH")}
              size="small"
              style={autoSearchInputStyles}
              InputProps={{
                ...params.InputProps,
                style: { height: "40px" },
                endAdornment: (
                  <InputAdornment position="end">
                    <Image src={searchIcon} alt="search" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: error ? "red" : undefined,
                  },

                  "&:hover fieldset": {
                    borderColor: error ? "red" : undefined,
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: error ? "red" : undefined,
                  },
                },
              }}
            />
          )}
        />
        <PlaceholderText>{t("CLIENT_INVOICING_NO_COMPANY")}</PlaceholderText>
      </BlockBody>
    </Block>
  );
};

SelectedCompany.propTypes = {
  t: PropTypes.func.isRequired,
  company: PropTypes.object,
  countryId: PropTypes.number,
  countryList: PropTypes.array,
  onSelectCompany: PropTypes.func.isRequired,
  onRemoveCompany: PropTypes.func.isRequired,
};

SelectedCompany.defaultProps = {
  company: null,
  countryId: null,
  countryList: [],
};

export default withTranslation()(SelectedCompany);
